import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import Layout from '../components/Layout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CheckCircle2, CreditCard, ArrowRight, Loader2, AlertCircle } from 'lucide-react';

const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = searchParams.get('session_id');
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (sessionId) {
      verifyPayment();
    } else {
      setError('No session ID found');
      setLoading(false);
    }
  }, [sessionId]);

  const verifyPayment = async () => {
    try {
      const res = await axios.get(`${API}/payments/status/${sessionId}`);
      setStatus(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to verify payment');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <div className="flex items-center justify-center min-h-[60vh]" data-testid="payment-success-page">
        <Card className="max-w-md w-full p-8 border border-slate-200 text-center space-y-6">
          {loading ? (
            <div className="space-y-4" data-testid="payment-verifying">
              <Loader2 size={48} className="animate-spin text-indigo-600 mx-auto" />
              <h2 className="text-xl font-bold text-slate-900">Verifying Payment...</h2>
              <p className="text-slate-500">Please wait while we confirm your payment</p>
            </div>
          ) : error ? (
            <div className="space-y-4" data-testid="payment-error">
              <AlertCircle size={48} className="text-rose-500 mx-auto" />
              <h2 className="text-xl font-bold text-slate-900">Verification Issue</h2>
              <p className="text-slate-500">{error}</p>
              <Button onClick={() => navigate('/credits')} className="bg-indigo-600 hover:bg-indigo-700 text-white">
                Back to Credits
              </Button>
            </div>
          ) : status?.payment_status === 'paid' ? (
            <div className="space-y-4" data-testid="payment-confirmed">
              <div className="mx-auto w-20 h-20 rounded-full bg-emerald-100 flex items-center justify-center">
                <CheckCircle2 size={44} className="text-emerald-600" />
              </div>
              <h2 className="text-2xl font-bold text-slate-900">Payment Successful!</h2>
              <p className="text-slate-500">Your credits have been added to your account.</p>
              <Card className="p-4 border border-emerald-200 bg-emerald-50">
                <div className="flex items-center justify-center gap-3">
                  <CreditCard size={20} className="text-emerald-600" />
                  <span className="text-lg font-bold text-emerald-800">+{status.credits} credits</span>
                </div>
                {status.amount && (
                  <p className="text-sm text-emerald-600 mt-1">
                    Paid ${status.amount.toFixed(2)} {status.currency?.toUpperCase()}
                  </p>
                )}
              </Card>
              <div className="flex gap-3 pt-2">
                <Button variant="outline" onClick={() => navigate('/credits')} className="flex-1" data-testid="back-to-credits-btn">
                  Buy More
                </Button>
                <Button onClick={() => navigate('/analytics')} className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="go-to-analytics-btn">
                  <ArrowRight size={16} className="mr-2" />
                  Dashboard
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-4" data-testid="payment-pending">
              <Loader2 size={48} className="text-amber-500 mx-auto" />
              <h2 className="text-xl font-bold text-slate-900">Payment Processing</h2>
              <p className="text-slate-500">Your payment is being processed. Credits will be added once confirmed.</p>
              <p className="text-sm text-slate-400">Status: {status?.payment_status || 'unknown'}</p>
              <Button onClick={verifyPayment} variant="outline" data-testid="retry-verify-btn">
                Check Again
              </Button>
            </div>
          )}
        </Card>
      </div>
    </Layout>
  );
};

export default PaymentSuccess;
